import html2canvas from 'html2canvas';
import { sanitizeExportColors } from '../../../../../utils/exportColors';
import { jsPDF } from 'jspdf';
import { downloadBlob } from '../../toolPageUtils';

const EXPORT_SCALE = 2;
const A4 = { widthMm: 210, heightMm: 297, marginMm: 5 };

/**
 * @param {HTMLElement} root
 */
function fixExportRendering(root) {
    root.style.maxHeight = 'none';
    root.style.overflow = 'visible';

    root.querySelectorAll('[data-export-hide]').forEach((el) => {
        el.remove();
    });
}

/**
 * @param {HTMLElement} element
 * @param {string} [scheduleRowsHtml]
 */
function cloneForExport(element, scheduleRowsHtml) {
    const width = element.offsetWidth || element.scrollWidth;

    const clone = element.cloneNode(true);
    const wrapper = document.createElement('div');
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.style.cssText = [
        'position:fixed',
        'left:-20000px',
        'top:0',
        `width:${width}px`,
        'overflow:visible',
        'pointer-events:none',
        'z-index:-1',
        'background:#ffffff',
    ].join(';');

    clone.style.width = `${width}px`;
    clone.style.maxWidth = `${width}px`;
    clone.style.transform = 'none';
    clone.style.overflow = 'visible';
    clone.style.background = '#ffffff';

    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);
    fixExportRendering(clone);

    if (scheduleRowsHtml) {
        const tbody = clone.querySelector('[data-export-schedule-body]');
        if (tbody) tbody.innerHTML = scheduleRowsHtml;
    }

    const height = Math.max(clone.scrollHeight, clone.offsetHeight, element.scrollHeight) + 4;
    wrapper.style.height = `${height}px`;
    return { clone, wrapper, width, height };
}

/**
 * @param {HTMLElement} element
 * @param {{ scheduleRowsHtml?: string }} [options]
 */
export async function captureEmiCalculatorCanvas(element, { scheduleRowsHtml } = {}) {
    const { clone, wrapper, width, height } = cloneForExport(element, scheduleRowsHtml);

    try {
        await document.fonts.ready;
        return await html2canvas(clone, {
            scale: EXPORT_SCALE,
            width,
            height,
            windowWidth: width,
            windowHeight: height,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: '#ffffff',
            onclone: (doc) => {
                const root = doc.querySelector('[data-emi-calc-export]');
                if (root instanceof HTMLElement) { fixExportRendering(root); sanitizeExportColors(root); }
            },
        });
    } finally {
        wrapper.remove();
    }
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {import('jspdf').jsPDF} pdf
 */
function addCanvasToMultiPagePdf(canvas, pdf) {
    const margin = A4.marginMm;
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const contentWidth = pdfWidth - margin * 2;
    const contentHeight = pdfHeight - margin * 2;
    const pageHeightPx = Math.floor((contentHeight / contentWidth) * canvas.width);

    let offsetY = 0;
    let pageIndex = 0;

    while (offsetY < canvas.height) {
        if (pageIndex > 0) pdf.addPage();

        const sliceHeight = Math.min(pageHeightPx, canvas.height - offsetY);
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;

        const ctx = pageCanvas.getContext('2d');
        if (!ctx) break;

        ctx.drawImage(
            canvas,
            0,
            offsetY,
            canvas.width,
            sliceHeight,
            0,
            0,
            canvas.width,
            sliceHeight,
        );

        const drawHeight = (sliceHeight / canvas.width) * contentWidth;
        pdf.addImage(
            pageCanvas.toDataURL('image/jpeg', 0.95),
            'JPEG',
            margin,
            margin,
            contentWidth,
            drawHeight,
            undefined,
            'FAST',
        );

        offsetY += sliceHeight;
        pageIndex += 1;
    }
}

/**
 * @param {HTMLElement} element
 * @param {string} filename
 * @param {{ scheduleRowsHtml?: string }} [options]
 */
export async function downloadEmiCalculatorPdf(element, filename = 'emi-calculator.pdf', options = {}) {
    const canvas = await captureEmiCalculatorCanvas(element, options);
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    addCanvasToMultiPagePdf(canvas, pdf);
    downloadBlob(pdf.output('blob'), filename);
}
